/**
 * GovGeocodeMigrationCard — Iter160
 *
 * Master-only 1-click migration: geocode any `gov_leads` rows that are
 * missing lat/lng so they appear on the Leads Map in production. Safe to
 * click repeatedly — the backend filters rows that already have coords.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Loader2, PlayCircle, CheckCircle2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GovGeocodeMigrationCard() {
  const [status, setStatus] = useState(null);
  const [running, setRunning] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/geocode/status-gov`);
      setStatus(data);
      // Auto-poll while a job is running
      if (data.last_job?.status === "running") setRunning(true);
      else setRunning(false);
    } catch { /* silent */ }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!running) return;
    const iv = setInterval(load, 4000);
    return () => clearInterval(iv);
  }, [running]);

  const start = async () => {
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/geocode/backfill-gov`, {});
      if (data.ok === false && data.reason === "job_already_running") {
        toast.info("Migração já em execução");
      } else {
        toast.success(`Migração iniciada · ${data.total} gov leads na fila`);
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Falha ao iniciar migração");
      setRunning(false);
    }
  };

  const job = status?.last_job;
  const missing = status?.gov_leads_missing_coords ?? 0;
  const withCoords = status?.gov_leads_with_coords ?? 0;
  const pct = job && job.total ? Math.round(((job.done + job.failed) / job.total) * 100) : 0;

  return (
    <div className="rounded-xl p-5 border" data-testid="gov-geocode-migration-card"
         style={{ borderColor: "rgba(59,130,246,0.25)", background: "rgba(59,130,246,0.06)" }}>
      <div className="flex items-center gap-2 mb-3">
        <MapPin className="w-4 h-4 text-[#3B82F6]" />
        <span className="text-[11px] uppercase tracking-wider text-[#3B82F6] font-bold">
          Gov Leads · Geocode Migration
        </span>
      </div>
      <p className="text-[12px] text-white/70 mb-3">
        Sincroniza os leads governamentais sem coordenadas para que apareçam no Leads Map.
        Seguro para rodar múltiplas vezes.
      </p>

      <div className="grid grid-cols-2 gap-2 mb-3">
        <Metric label="No mapa" value={withCoords} color="#10B981" testid="gov-geocode-with" />
        <Metric label="Sem coord." value={missing} color="#F59E0B" testid="gov-geocode-missing" />
      </div>

      {job && (
        <div className="mb-3 rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid="gov-geocode-progress">
          <div className="flex items-center justify-between mb-1 text-[11px]">
            <span className="text-white/70">
              {job.status === "running" ? "Em execução" : "Último job"} · {job.done + job.failed}/{job.total || "?"}
            </span>
            <span className="font-bold text-white tabular-nums">{pct}%</span>
          </div>
          <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
            <div className="h-full transition-all"
                 style={{ width: `${pct}%`, background: job.status === "done" ? "#10B981" : "#3B82F6" }} />
          </div>
          {job.failed > 0 && (
            <div className="mt-1 text-[10.5px] text-amber-300">
              {job.failed} falhas (endereço ambíguo)
            </div>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={start}
        disabled={running || missing === 0}
        data-testid="gov-geocode-run-btn"
        className="w-full h-10 rounded-lg font-bold text-[12.5px] text-white inline-flex items-center justify-center gap-2 disabled:opacity-50 transition-all"
        style={{ background: missing === 0 ? "rgba(16,185,129,0.15)" : "linear-gradient(135deg,#3B82F6 0%,#1D4ED8 100%)" }}
      >
        {running ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> Migrando…</>
        ) : missing === 0 ? (
          <><CheckCircle2 className="w-4 h-4" /> Tudo sincronizado</>
        ) : (
          <><PlayCircle className="w-4 h-4" /> Rodar migração ({missing} pendentes)</>
        )}
      </button>
    </div>
  );
}

function Metric({ label, value, color, testid }) {
  return (
    <div className="rounded-lg border border-white/10 bg-black/20 p-2.5" data-testid={testid}>
      <div className="text-[9.5px] uppercase tracking-widest text-white/50 font-bold">{label}</div>
      <div className="text-[18px] font-extrabold tabular-nums" style={{ color }}>{value?.toLocaleString?.() ?? value}</div>
    </div>
  );
}
